import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useDivisionSave } from "@/queries/use-divisitions.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiDivision } from "@/types/division.types.ts";
import { Button, Input, useModals, Modal, ModalProps, Stack } from "@skailer-com/uikit";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface DivisionEditModalProps extends ModalProps {
  division: ApiDivision;
}

export function DivisionEditModal({ division, ...props }: DivisionEditModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const divisionSchema = z.object({
    title: z.string().min(1, t("errors:field_required")),
  });

  type DivisionSchema = z.infer<typeof divisionSchema>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<DivisionSchema>({
    mode: "onSubmit",
    resolver: zodResolver(divisionSchema),
  });

  const divisionSave = useDivisionSave(control);

  const onSubmit = handleSubmit((divisionData) => {
    divisionSave.mutate(
      {
        id: division.id,
        title: divisionData.title,
      },
      {
        onSuccess: (data) => {
          if (division.id == 0) {
            analyticsService.trackEvent(analyticEvents.divisions.created, {
              [analyticProperties.id]: data.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.divisions.edited, {
              [analyticProperties.id]: division.id,
            });
          }
          close(id);
        },
      }
    );
  });

  return (
    <>
      <Modal
        {...props}
        title={t("core:division")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              type={"submit"}
              size={"lg"}
              isLoading={divisionSave.isPending}
              onClick={onSubmit}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Input
            required={true}
            label={t("common:title")}
            autoFocus={true}
            type="text"
            {...register("title", {
              value: division.title,
            })}
            error={errors.title?.message}
          />
        </form>
      </Modal>
    </>
  );
}
